import React from 'react';
import { IArticle } from '../../types';
import {
  Card,
  FlexGrid,
  getClassNameFactory,
  Heading,
  Link,
  TextBoldness,
  Typography,
} from '@dcl/ui';
import './Article.css';
import ReactHtmlParser from 'react-html-parser';
import { boldTypes, types } from '@dcl/ui/dist/elements/Heading';
import { TypographyTags } from '@dcl/ui/dist/elements/Typography';
import { NewTabIcon } from '../../../../icons/NewTabIcon';
import { PDFIcon } from '../../../../icons/PDFIcon';
import { ArticleIcon } from '../../../../icons/ArticleIcon';
import { Badge } from '../../../../../Badge/Badge';
import { FileIcon } from '../../../../icons/FileIcon';
import { article_images } from '../../../../mock';

export const Article: React.FC<IArticle> = (props) => {
  const {
    articleHeading,
    articleSegmentationTitle,
    articlesegmentationText,
    articleFormat,
    articleSize,
    articleReadTime,
    articleDescription,
    articleLinkHref,
    articleLinkText,
    isNewTab,
    badge,
  } = props;

  const getClassName = getClassNameFactory(Article.displayName);

  const getArticleSize = articleSize && (
    <Typography
      tag={TypographyTags.SPAN}
      boldness={TextBoldness.Light}
      className={getClassName({ descendantName: 'articleFormat' })}
    >
      {articleSize}
    </Typography>
  );
  const getArticleIcon = () => {
    switch (articleFormat.toLocaleLowerCase()) {
      case 'article':
        return <ArticleIcon />;
      case 'pdf':
        return <PDFIcon />;
      case 'file':
        return <FileIcon />;
      default:
        return;
    }
  };

  const getArticleFormat = () => {
    const urlHostname =
      window.location.hostname === 'localhost'
        ? 'https://www-tr-com-qa-ams.ewp.thomsonreuters.com'
        : 'https://' + window.location.host;
    const randomIndex = Math.floor(Math.random() * article_images.length);
    return urlHostname + article_images[randomIndex];
  };

  const segmentationRow = (
    <div className={getClassName({ descendantName: 'Info' })}>
      <div className={getClassName({ descendantName: 'Icon' })}>
        {getArticleIcon()}
      </div>
      <Typography
        tag={TypographyTags.SPAN}
        boldness={TextBoldness.Light}
        className={getClassName({
          descendantName: 'articleFormat',
        })}
      >
        {articleFormat}
      </Typography>
      {getArticleSize}
      <Typography tag={TypographyTags.SPAN} boldness={TextBoldness.Regular}>
        {articleReadTime}
      </Typography>
    </div>
  );

  const segmentationTitleRow = (
    segmentationTitle: string,
    segmentationText: string
  ) => {
    return segmentationTitle &&
      segmentationTitle.length > 0 &&
      segmentationTitle.toLocaleLowerCase() === 'return type' ? (
      <>
        <FlexGrid.Col xs={4} sm={4} md={4}>
          <Typography
            tag={TypographyTags.SPAN}
            boldness={TextBoldness.Regular}
            className={getClassName({
              descendantName: 'segmentationTitle',
            })}
          >
            {segmentationTitle}:
          </Typography>
        </FlexGrid.Col>
        <FlexGrid.Col xs={8} sm={8} md={8}>
          <Typography
            tag={TypographyTags.SPAN}
            boldness={TextBoldness.Light}
            className={getClassName({
              descendantName: 'segmentationText',
            })}
          >
            {segmentationText}
          </Typography>
        </FlexGrid.Col>
      </>
    ) : (
      <>
        <FlexGrid.Col xs={3} sm={3} md={3}>
          <Typography
            tag={TypographyTags.SPAN}
            boldness={TextBoldness.Regular}
            className={getClassName({
              descendantName: 'segmentationTitle',
            })}
          >
            {segmentationTitle}:
          </Typography>
        </FlexGrid.Col>
        <FlexGrid.Col xs={9} sm={9} md={9}>
          <Typography
            tag={TypographyTags.SPAN}
            boldness={TextBoldness.Light}
            className={getClassName({
              descendantName: 'segmentationText',
            })}
          >
            {segmentationText}
          </Typography>
        </FlexGrid.Col>
      </>
    );
  };

  return (
    <div className={getClassName({ descendantName: 'tile' })}>
      <Card.CardBase size="small" data-testid="ArticleTile">
        <div className={getClassName({ descendantName: 'articleBadge' })}>
          <Badge badge={badge}></Badge>
        </div>
        <div
          className={getClassName({
            descendantName: 'imageConatiner',
          })}
          style={{ backgroundImage: `url(${getArticleFormat()})` }}
        ></div>

        <FlexGrid
          className={getClassName({ descendantName: 'cardContentContainer' })}
        >
          <FlexGrid.Row>
            <FlexGrid.Col
              xs={12}
              sm={12}
              md={12}
              className={getClassName({ descendantName: 'articleHeading' })}
            >
              <Heading
                type={types.Medium}
                boldType={boldTypes.medium}
                className={getClassName({
                  descendantName: 'articleTitleHeading',
                })}
              >
                <div title={articleHeading}>{articleHeading}</div>
              </Heading>
            </FlexGrid.Col>
          </FlexGrid.Row>
          <FlexGrid.Row className={getClassName({ descendantName: 'eyeBrow' })}>
            {segmentationTitleRow(
              articleSegmentationTitle,
              articlesegmentationText
            )}

            <FlexGrid.Col xs={3} sm={3} md={3}>
              <Typography
                tag={TypographyTags.SPAN}
                boldness={TextBoldness.Regular}
                className={getClassName({ descendantName: 'articleFormat' })}
              >
                {'Format'}:
              </Typography>
            </FlexGrid.Col>

            <FlexGrid.Col xs={9} sm={9} md={9}>
              {segmentationRow}
            </FlexGrid.Col>
          </FlexGrid.Row>

          <FlexGrid.Row
            className={getClassName({ descendantName: 'description' })}
          >
            <FlexGrid.Col xs={12} sm={12} md={12}>
              <Typography
                tag={TypographyTags.SPAN}
                boldness={TextBoldness.Regular}
                className={getClassName({
                  descendantName: 'articleDescription',
                })}
              >
                <div title={articleDescription}>
                  {ReactHtmlParser(articleDescription)}
                </div>
              </Typography>
            </FlexGrid.Col>
          </FlexGrid.Row>
        </FlexGrid>
        <FlexGrid.Row className={getClassName({ descendantName: 'link' })}>
          <FlexGrid.Col xs={12} sm={12} md={12}>
            {isNewTab ? (
              <Link
                href={articleLinkHref}
                newWindow={isNewTab}
                iconRight={<NewTabIcon />}
              >
                {articleLinkText}
              </Link>
            ) : (
              <Link href={articleLinkHref} newWindow={isNewTab}>
                {articleLinkText}
              </Link>
            )}
          </FlexGrid.Col>
        </FlexGrid.Row>
      </Card.CardBase>
    </div>
  );
};

Article.displayName = 'Article';
